import { FC } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { BorderedSelect } from '@components';
import { AssessmentStatus, TimeFilter } from '@libs/constants';
import { Trans, useTrans } from '@libs/i18n';
import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';

interface AssessmentsFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: AssessmentStatus | 'All Status';
  onStatusFilterChange: (value: AssessmentStatus | 'All Status') => void;
  timeFilter: TimeFilter | 'All Time';
  onTimeFilterChange: (value: TimeFilter | 'All Time') => void;
  onClearFilters: () => void;
}

export const AssessmentsFilters: FC<AssessmentsFiltersProps> = observer(
  ({
    searchTerm,
    onSearchChange,
    statusFilter,
    onStatusFilterChange,
    timeFilter,
    onTimeFilterChange,
    onClearFilters,
  }) => {
    const { t } = useTrans();
    const theme = useTheme();

    return (
      <Paper
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          border: 'none',
          boxShadow: 'none',
          backgroundColor: 'transparent',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'nowrap',
            width: '100%',
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('app.assessments.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlassIcon size={20} color={theme.palette.text.primary} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => onSearchChange('')} size="small">
                    <XIcon size={20} color={theme.palette.text.primary} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: '40%',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: theme.palette.grey[200],
              },
            }}
          />

          <BorderedSelect
            value={statusFilter}
            onChange={(e) =>
              onStatusFilterChange(e.target.value as AssessmentStatus | 'All Status')
            }
            minWidth={140}
            sx={{ width: '25%', minWidth: '140px' }}
          >
            <MenuItem value="All Status">
              <Trans i18nKey="app.assessments.status.all" />
            </MenuItem>
            <MenuItem value={AssessmentStatus.DRAFT}>
              <Trans i18nKey="app.assessments.status.draft" />
            </MenuItem>
            <MenuItem value={AssessmentStatus.PROCESSING}>
              <Trans i18nKey="app.assessments.status.processing" />
            </MenuItem>
            <MenuItem value={AssessmentStatus.COMPLETED}>
              <Trans i18nKey="app.assessments.status.completed" />
            </MenuItem>
            <MenuItem value={AssessmentStatus.SUBMITTED}>
              <Trans i18nKey="app.assessments.status.submitted" />
            </MenuItem>
            <MenuItem value={AssessmentStatus.FAILED}>
              <Trans i18nKey="app.assessments.status.failed" />
            </MenuItem>
          </BorderedSelect>
          <BorderedSelect
            value={timeFilter}
            onChange={(e) => onTimeFilterChange(e.target.value as TimeFilter | 'All Time')}
            minWidth={140}
            sx={{ width: '25%', minWidth: '140px' }}
          >
            <MenuItem value="All Time">
              <Trans i18nKey="app.assessments.filters.allTime" />
            </MenuItem>
            <MenuItem value={TimeFilter.TODAY}>
              <Trans i18nKey="app.assessments.filters.today" />
            </MenuItem>
            <MenuItem value={TimeFilter.THIS_WEEK}>
              <Trans i18nKey="app.assessments.filters.thisWeek" />
            </MenuItem>
            <MenuItem value={TimeFilter.THIS_MONTH}>
              <Trans i18nKey="app.assessments.filters.thisMonth" />
            </MenuItem>
            <MenuItem value={TimeFilter.THIS_QUARTER}>
              <Trans i18nKey="app.assessments.filters.thisQuarter" />
            </MenuItem>
          </BorderedSelect>

          <Button
            variant="text"
            onClick={onClearFilters}
            startIcon={<XIcon size={20} color={theme.palette.text.primary} />}
            sx={{
              width: '10%',
              minWidth: '100px',
              textTransform: 'none',
              gap: '4px',
              color: theme.palette.text.primary,
              backgroundColor: 'transparent',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: 'transparent',
                color: theme.palette.text.primary,
              },
            }}
          >
            <Trans i18nKey="app.assessments.clearAll" />
          </Button>
        </Box>
      </Paper>
    );
  },
);
